// 일정 관련 타입 정의

// 일정 카테고리
export enum ScheduleCategory {
  // 이용자 관련
  USER_OUTING = '외출',
  USER_OVERNIGHT = '외박',
  USER_VISIT = '면회',
  USER_INDEPENDENCE = '자립',
  USER_DISCHARGE = '퇴소',
  
  // 종사자 관련
  STAFF_ANNUAL = '연가',
  STAFF_PUBLIC = '공가',
  STAFF_SICK = '병가',
  STAFF_HALF = '반가',
  STAFF_FAMILY_CARE = '가족돌봄',
  
  // 기타
  MEETING = '회의',
  EVENT = '행사',
  TRAINING = '교육',
  MAINTENANCE = '점검'
}

// 일정 우선순위
export enum SchedulePriority {
  LOW = '낮음',
  MEDIUM = '보통',
  HIGH = '높음',
  URGENT = '긴급'
}

// 일정 상태
export enum ScheduleStatus {
  SCHEDULED = '예정',
  IN_PROGRESS = '진행중',
  COMPLETED = '완료',
  CANCELLED = '취소',
  POSTPONED = '연기'
}

// 반복 유형
export enum RecurrenceType {
  NONE = '없음',
  DAILY = '매일',
  WEEKLY = '매주',
  MONTHLY = '매월',
  YEARLY = '매년'
}

// 기본 일정 인터페이스
export interface Schedule {
  id: number;
  title: string;
  description?: string;
  start_date: string; // YYYY-MM-DD HH:mm:ss 형식
  end_date: string;
  category: ScheduleCategory;
  priority: SchedulePriority;
  status: ScheduleStatus;
  location?: string;
  participants?: string; // 참가자 목록 (콤마로 구분)
  recurrence_type: RecurrenceType;
  recurrence_end?: string; // 반복 종료일
  is_all_day: boolean;
  created_by: string;
  created_at: string;
  updated_at?: string;
}

// 휴일 정보
export interface Holiday {
  id: number;
  name: string;
  date: string; // YYYY-MM-DD 형식
  type: 'national' | 'company' | 'custom'; // 국경일, 회사 휴일, 사용자 정의
  is_holiday: boolean; // 실제 휴무일 여부
  description?: string;
  created_at: string;
}

// 일정 생성 요청
export interface CreateScheduleRequest {
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  category: ScheduleCategory;
  priority: SchedulePriority;
  location?: string;
  participants?: string;
  recurrence_type: RecurrenceType;
  recurrence_end?: string;
  is_all_day: boolean;
}

// 일정 수정 요청
export interface UpdateScheduleRequest extends CreateScheduleRequest {
  id: number;
  status: ScheduleStatus;
}

// 일정 필터 옵션
export interface ScheduleFilters {
  start_date?: string;
  end_date?: string;
  category?: ScheduleCategory | 'ALL';
  priority?: SchedulePriority | 'ALL';
  status?: ScheduleStatus | 'ALL';
  search?: string;
  created_by?: string;
}

// 일정 정렬 옵션
export interface ScheduleSortOptions {
  field: keyof Schedule;
  order: 'asc' | 'desc';
}

// 캘린더 이벤트 (캘린더 컴포넌트용)
export interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  category: ScheduleCategory;
  priority: SchedulePriority;
  status: ScheduleStatus;
  description?: string;
  location?: string;
  participants?: string;
}

// 캘린더 뷰 타입
export type CalendarView = 'month' | 'week' | 'day' | 'list';

// 월별 일정 요약
export interface MonthlyScheduleSummary {
  year: number;
  month: number;
  total_schedules: number;
  schedules_by_category: Record<ScheduleCategory, number>;
  schedules_by_status: Record<ScheduleStatus, number>;
  holidays: Holiday[];
  busy_days: string[]; // 일정이 많은 날짜들
}

// 일정 통계
export interface ScheduleStatistics {
  total_schedules: number;
  completed_schedules: number;
  pending_schedules: number;
  cancelled_schedules: number;
  upcoming_schedules: number;
  overdue_schedules: number;
  completion_rate: number;
  category_distribution: Record<ScheduleCategory, number>;
  priority_distribution: Record<SchedulePriority, number>;
}

// API 응답 타입들
export interface ScheduleResponse {
  success: boolean;
  data: Schedule[];
  message?: string;
}

export interface HolidayResponse {
  success: boolean;
  data: Holiday[];
  message?: string;
}

export interface SchedulePaginationResponse {
  data: Schedule[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// 일정 충돌 체크 결과
export interface ScheduleConflict {
  hasConflict: boolean;
  conflictingSchedules: Schedule[];
  message?: string;
}

// 일정 알림 설정
export interface ScheduleReminder {
  id: number;
  schedule_id: number;
  remind_at: string; // 알림 시간
  is_sent: boolean;
  created_at: string;
}

// 일정 참가자
export interface ScheduleParticipant {
  id: number;
  schedule_id: number;
  user_name: string;
  email?: string;
  status: 'invited' | 'accepted' | 'declined' | 'tentative';
  created_at: string;
}